/**
 * KAN-759: формирование заказа (корзины) в модалке CRM: поиск товара
 * каталога, количество, удаление, итог, "Оформить заказ" с подтверждением.
 * Оформление недоступно при пустой корзине; после оформления модалка
 * закрывается. Цена позиции только из каталога (считает backend).
 * KAN-903: onPlaced - отдельный обработчик оформления (по умолчанию onClose),
 * footer - кнопки под заказом (окно "Успех": "Пропустить").
 */
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useOrderEditor } from '@/hooks/useOrderEditor';
import type { OrderAction, OrderDetail } from '@/types/order';
import { OrderActions } from './OrderActions';
import { OrderDialogShell } from './OrderDialogShell';
import { OrderErrorAlert, OrderItemsPanel } from './OrderItemsPanel';

const COMPOSE_ACTIONS: readonly OrderAction[] = ['place'];

interface OrderComposeDialogProps {
  /** null: модалка закрыта. */
  orderId: string | null;
  onClose: () => void;
  /** Заказ оформлен; по умолчанию onClose. */
  onPlaced?: () => void;
  footer?: ReactNode;
}

function ComposeBody({ order, onPlaced }: { order: OrderDetail; onPlaced: () => void }) {
  const editor = useOrderEditor(order.id);
  const empty = order.items.length === 0;

  return (
    <div className="space-y-3">
      <div className="px-6">
        <OrderErrorAlert error={editor.error} />
      </div>
      <OrderItemsPanel order={order} editor={editor} />
      <div className="flex items-center justify-end gap-3 px-6">
        {editor.pending && <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Сохранение" />}
        <OrderActions
          permissions={order.permissions}
          pending={editor.pending}
          actions={COMPOSE_ACTIONS}
          disabledActions={empty ? COMPOSE_ACTIONS : []}
          onAction={(action) => editor.runAction(action, onPlaced)}
        />
      </div>
    </div>
  );
}

export function OrderComposeDialog({ orderId, onClose, onPlaced = onClose, footer }: OrderComposeDialogProps) {
  return (
    <OrderDialogShell orderId={orderId} onClose={onClose} testId="order-compose-dialog" footer={footer}>
      {(order) => <ComposeBody order={order} onPlaced={onPlaced} />}
    </OrderDialogShell>
  );
}
